import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  Checkbox,
  Icon,
  InlineMessage,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { setConsultActionAssigneeLoading } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_APPLICATION_OWNER,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  CAMPAIGNTYPE_USERMANAGER,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import {
  BULK_ACTION_FOOTER_OBJECTS,
  BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS,
  BULK_ACTION_OBJ_ACCOUNTS,
  BULK_ACTION_OBJ_ENTITLEMENTS,
  BULK_ACTION_OBJ_ROLES,
} from '../constant';
import { BULK_ACTION_CONSULT } from '../mockData';

import BulkActionConsultList from './BulkActionConsultList';
import {
  BULK_ACTION_CONSULT_ICON_MAP_FOR_STATS,
  BULK_ACTION_OBJ_PARENT_ENTITLEMENTS,
  BULK_ACTION_OBJ_USERS,
  COMMENT_MINIMUM_LENGTH,
} from './constant';
import invertedMessages from './messages';

function BulkActionConsultBody({
  bulkFooterStats: originalBulkFooterStats,
  bulkActionConsult,
  setBulkActionConsult,
  handleCloseBulkActionConsult,
  handleCloseBukActionConsultAlert,
  bulkPredefinedComments,
}) {
  const intl = useIntl();
  const l = useInvertedLocalize(invertedMessages);
  const dispatch = useDispatch();
  const {
    global: { certKey },
    lineActions: { consultActionAssignee, reassignConfigMap },
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);
  const { predefinedComments: { allowEditPredefinedComments = false } = {} } =
    useSelector((state) => state?.certifications?.details?.core || {});
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const { messages } = useSelector((state) => state?.user);

  // Filter bulkFooterStats based on campaign type
  const getFilteredBulkFooterStats = () => {
    const campaignName = CAMPAIGN_TYPES[campaignType];
    const filteredStats = { ...originalBulkFooterStats };

    switch (campaignName) {
      case CAMPAIGNTYPE_ENTITLEMENTOWNER:
        delete filteredStats.totalChildEntitlements;

        if (!reassignConfigMap.showConsultStep1) {
          delete filteredStats[BULK_ACTION_OBJ_PARENT_ENTITLEMENTS];
        }

        if (!reassignConfigMap.showConsultStep2) {
          delete filteredStats[BULK_ACTION_OBJ_ACCOUNTS];
        }

        break;
      case CAMPAIGNTYPE_SERVICEACCOUNT:
        if (!reassignConfigMap.showConsultStep1) {
          delete filteredStats[BULK_ACTION_OBJ_ACCOUNTS];
        }

        if (!reassignConfigMap.showConsultStep2) {
          delete filteredStats[BULK_ACTION_OBJ_ROLES];
          delete filteredStats[BULK_ACTION_OBJ_ENTITLEMENTS];
        }

        break;
      case CAMPAIGNTYPE_APPLICATION_OWNER:
        if (!reassignConfigMap.showConsultStep2) {
          delete filteredStats[BULK_ACTION_OBJ_ROLES];
          delete filteredStats[BULK_ACTION_OBJ_ENTITLEMENTS];
          delete filteredStats[BULK_ACTION_OBJ_ACCOUNTS];
        }

        break;
      case CAMPAIGNTYPE_USERMANAGER:
        if (!reassignConfigMap.showConsultStep1) {
          delete filteredStats[BULK_ACTION_OBJ_USERS];
        }

        if (!reassignConfigMap.showConsultStep2) {
          delete filteredStats[BULK_ACTION_OBJ_ROLES];
          delete filteredStats[BULK_ACTION_OBJ_ENTITLEMENTS];
          delete filteredStats[BULK_ACTION_OBJ_ACCOUNTS];
        }

        break;
      default:
        break;
    }

    return filteredStats;
  };

  const bulkFooterStats = getFilteredBulkFooterStats();

  const isConsultActionAssigneeLoading = Boolean(
    consultActionAssignee?.isLoading
  );
  const isConsultActionAssigneeError = Boolean(
    consultActionAssignee?.errorMessage?.error !== undefined &&
      consultActionAssignee?.errorMessage?.error !== null &&
      consultActionAssignee?.errorMessage?.error?.trim()?.length !== 0
  );

  useEffect(() => {
    if (bulkActionConsult?.isOpen) {
      dispatch(setConsultActionAssigneeLoading(false));
    }
  }, [bulkActionConsult?.isOpen]);

  const totalCountOfItems = Object?.values(bulkFooterStats)?.reduce(
    (total, individualCount) => total + individualCount,
    0
  );

  const handleCommentDescription = (text) => {
    setBulkActionConsult((prev) => ({
      ...prev,
      comment: text,
    }));
  };

  const handleHasAgreedChange = () => {
    setBulkActionConsult((prev) => ({
      ...prev,
      hasAgreed: !prev.hasAgreed,
    }));
  };

  const consultObjectKeys = Object?.keys(bulkFooterStats);
  const shouldShowInlineMessage = Boolean(
    consultObjectKeys.length === 1 &&
      consultObjectKeys.includes(BULK_ACTION_OBJ_USERS)
  );

  const renderConsultActionBody = () => {
    if (isConsultActionAssigneeLoading) {
      return (
        <Box className='BulkActionConsultBody-loaderWrapper'>
          <Loader
            kind='dots'
            format='inline'
            color='brand'
            size='large'
            className='BulkActionConsultBody-loader'
          />
        </Box>
      );
    }

    if (isConsultActionAssigneeError) {
      return (
        <Box className='AccessRequestHistoryBody-wrapper'>
          <div className='ReviewAndSubmitModal-bodyError'>
            <Typography kind='h2'>
              {messages['COULD_NOT_LOAD.ATTESTATIONS'] ||
                localize(intl, getMessageKey('COULD_NOT_LOAD'))}
            </Typography>
            <Typography kind='body2'>
              {messages['Failed.MODAL.ATTESTATIONS'] ||
                localize(intl, getMessageKey('Failed.MODAL'))}
            </Typography>
            <div className='ReviewAndSubmitModal-bodyErrorButtons'>
              <Button
                type='button'
                kind='ghost'
                size='medium'
                onClick={handleCloseBulkActionConsult}>
                {messages['CloseLabel.ATTESTATIONS'] ||
                  localize(intl, getMessageKey('CloseLabel'))}
              </Button>
            </div>
          </div>
        </Box>
      );
    }

    return (
      <>
        <Box tag='div' className='BulkActionConsultBody-stats'>
          <Typography
            kind='label'
            className='BulkActionConsultBody-statsHeading'>
            {`${
              messages.CERT_BULK_ITEM_TO_CONSULT ||
              localize(intl, getMessageKey('CERT_BULK_ITEM_TO_CONSULT'))
            } (${totalCountOfItems})`}
            {shouldShowInlineMessage &&
              (() => {
                let messageKey = '';
                const isMultiple = isEOCampaign
                  ? bulkFooterStats?.[BULK_ACTION_OBJ_PARENT_ENTITLEMENTS] > 1
                  : bulkFooterStats?.[BULK_ACTION_OBJ_USERS] > 1;

                if (isMultiple) {
                  messageKey = isEOCampaign
                    ? 'CERT_BULK_CONSULT_TOOLTIP_MULTIPLE_EO'
                    : 'CERT_BULK_CONSULT_TOOLTIP_MULTIPLE';
                } else {
                  messageKey = isEOCampaign
                    ? 'CERT_BULK_CONSULT_TOOLTIP_SINGLE_EO'
                    : 'CERT_BULK_CONSULT_TOOLTIP_SINGLE';
                }

                return (
                  <Tooltip
                    openDelay={500}
                    text={
                      messages.messageKey ||
                      localize(intl, getMessageKey(messageKey))
                    }
                    maxWidth='20rem'
                    placement='right'
                    offset={8}
                    trigger={
                      <Icon
                        kind='AlertInfoFilledSeparateColors'
                        color='--color-foreground-info-medium'
                        size='small'
                        className='BulkActionConsultBody-statsHeadingIcon'
                      />
                    }
                  />
                );
              })()}
          </Typography>
          <div className='BulkActionConsultBody-statsObjects'>
            {consultObjectKeys?.map((key) => {
              const messageKey =
                BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS?.[
                  `${BULK_ACTION_FOOTER_OBJECTS?.[key]}${
                    bulkFooterStats?.[key] > 1 ? 's' : ''
                  }`
                ];

              return (
                <Typography
                  key={key}
                  kind='body2'
                  className='BulkActionConsultBody-statsContent'>
                  <Icon
                    kind={BULK_ACTION_CONSULT_ICON_MAP_FOR_STATS?.[key].icon}
                    size='medium'
                  />
                  {`${bulkFooterStats?.[key]} ${
                    messages.messageKey ||
                    localize(intl, getMessageKey(messageKey))
                  }`}
                </Typography>
              );
            })}
          </div>
        </Box>
        <Box tag='div' className='BulkActionConsultBody-assigneeBox'>
          <Typography
            kind='label'
            className='BulkActionConsultBody-statsHeading'>
            {bulkActionConsult?.consultWith.length === 0 && (
              <Box
                className={
                  bulkActionConsult?.showAlert &&
                  bulkActionConsult?.consultWith.length === 0
                    ? 'BulkActionConsultBody-asterisk--isCritical BulkActionConsultBody-asterisk'
                    : 'BulkActionConsultBody-asterisk'
                }>
                *
              </Box>
            )}
            {messages['BULKACTION.consult.with.ATTESTATIONS'] ||
              localize(intl, getMessageKey('BULKACTION.consult.with'))}
          </Typography>
          <BulkActionConsultList
            isSubmitting={bulkActionConsult?.isSubmitting}
            consultWith={bulkActionConsult?.consultWith}
            isCritical={
              bulkActionConsult?.consultWith === '' &&
              bulkActionConsult?.showAlert
            }
            setBulkActionConsult={setBulkActionConsult}
            certKey={certKey}
            showDefaultCertifier={false}
            defaultReassignee={reassignConfigMap?.defaultReassignee}
            defaultAssigneName={reassignConfigMap?.defaultReassigneeName}
            placeholder={
              messages['BULKACTION.choose.assignee.ATTESTATIONS'] ||
              localize(intl, getMessageKey('BULKACTION.choose.assignee'))
            }
            certifierUserKey={reassignConfigMap?.certifierUserKey}
          />
        </Box>
        <CertsModalCommentBox
          defaultComments={bulkPredefinedComments}
          isDisabled={bulkActionConsult?.isSubmitting}
          showAlert={
            bulkActionConsult?.showAlert &&
            bulkActionConsult?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
              COMMENT_MINIMUM_LENGTH
          }
          onChange={handleCommentDescription}
          clearModal={bulkActionConsult?.isOpen}
          label={l('Comments')}
          allowEditPredefinedComments={allowEditPredefinedComments}
        />
        <div className='BulkActionsBody-checkBoxDiv'>
          <Checkbox
            id='bulk-action-consult-checkbox'
            className='BulkActionBody-consultCheckbox'
            name='checkbox-error'
            kind='singleLine'
            dataTestId='checkbox-error'
            isDisabled={bulkActionConsult?.isSubmitting}
            isChecked={bulkActionConsult?.hasAgreed}
            isRequired={!bulkActionConsult?.hasAgreed}
            isCritical={
              bulkActionConsult?.showAlert && !bulkActionConsult?.hasAgreed
            }
            onChange={handleHasAgreedChange}
            label={
              messages['BULKACTION.confirm.message.ATTESTATIONS'] ||
              localize(intl, getMessageKey('BULKACTION.confirm.message'))
            }
            textPosition='after'
            HelperText={
              bulkActionConsult?.showAlert &&
              !bulkActionConsult?.hasAgreed && (
                <InlineMessage
                  text={
                    messages['Required.field.ATTESTATIONS'] ||
                    localize(intl, getMessageKey('Required.field'))
                  }
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              )
            }
          />
        </div>
      </>
    );
  };

  const isAlertVisible = Boolean(
    bulkActionConsult?.showAlert && !bulkActionConsult?.isSubmitting
  );

  const commonBodyClass = classnames(
    'BulkActionConsultBody',
    isAlertVisible && 'BulkActionConsultBody-alertBox--1'
  );

  return (
    <>
      <AlertBanner
        colorTheme='Critical'
        title={
          messages['Required.fields.missing.ATTESTATIONS'] ||
          localize(intl, getMessageKey('Required.fields.missing'))
        }
        description=''
        isVisible={
          bulkActionConsult?.showAlert && !bulkActionConsult?.isSubmitting
        }
        isColonVisible={false}
        onCancel={handleCloseBukActionConsultAlert}
        isFloating
        position={{
          type: 'absolute',
          top: '0',
        }}
      />
      <div className={commonBodyClass}>{renderConsultActionBody()}</div>
    </>
  );
}

BulkActionConsultBody.propTypes = {
  bulkFooterStats: PropTypes.shape({}),
  bulkActionConsult: PropTypes.shape(BULK_ACTION_CONSULT).isRequired,
  setBulkActionConsult: PropTypes.func.isRequired,
  handleCloseBulkActionConsult: PropTypes.func.isRequired,
  handleCloseBukActionConsultAlert: PropTypes.func,
  bulkPredefinedComments: PropTypes.arrayOf(PropTypes.string),
};

BulkActionConsultBody.defaultProps = {
  bulkFooterStats: {},
  handleCloseBukActionConsultAlert: () => {},
  bulkPredefinedComments: [],
};

export default BulkActionConsultBody;
